import { useMemo } from 'react';
import {
  APP_NAME,
  MULTI_SHOP_BRAND_NAME,
  SITE_FOOTER_FALLBACK,
} from '@core/config/constants';
import {
  MULTI_SHOP_ENABLED,
  getFirstShopId,
} from '@core/config/shops';
import type { TenantShop } from './types';
import { useStoreDisplayName } from './useStoreDisplayName';
import { useTenantShops } from './TenantShopsContext';

export type FooterSocialLink = {
  id: 'instagram' | 'whatsapp' | 'facebook' | 'youtube';
  label: string;
  href: string;
};

export type FooterStoreModel = {
  brandName: string;
  aboutText: string;
  contact: {
    addressLines: string[];
    phone: string | null;
    email: string | null;
  };
  socialLinks: FooterSocialLink[];
  copyrightYear: number;
};

function buildSocialLinks(): FooterSocialLink[] {
  const links: FooterSocialLink[] = [];

  if (SITE_FOOTER_FALLBACK.instagram) {
    links.push({
      id: 'instagram',
      label: 'Instagram',
      href: SITE_FOOTER_FALLBACK.instagram,
    });
  }
  if (SITE_FOOTER_FALLBACK.whatsapp) {
    links.push({
      id: 'whatsapp',
      label: 'WhatsApp',
      href: SITE_FOOTER_FALLBACK.whatsapp,
    });
  }
  if (SITE_FOOTER_FALLBACK.facebook) {
    links.push({
      id: 'facebook',
      label: 'Facebook',
      href: SITE_FOOTER_FALLBACK.facebook,
    });
  }
  if (SITE_FOOTER_FALLBACK.youtube) {
    links.push({
      id: 'youtube',
      label: 'YouTube',
      href: SITE_FOOTER_FALLBACK.youtube,
    });
  }

  return links;
}

function buildAddressLines(shop: TenantShop): string[] {
  const lines: string[] = [];
  if (shop.businessAddress) lines.push(shop.businessAddress);
  const cityState = [shop.city, shop.state].filter(Boolean).join(', ').trim();
  if (cityState) lines.push(cityState);
  if (shop.pincode?.trim()) lines.push(shop.pincode.trim());
  return lines;
}

export function useFooterStoreContent(): FooterStoreModel {
  const { shops } = useTenantShops();
  const displayName = useStoreDisplayName();

  return useMemo(() => {
    const year = new Date().getFullYear();
    const socialLinks = buildSocialLinks();

    if (MULTI_SHOP_ENABLED) {
      const brand = MULTI_SHOP_BRAND_NAME;
      const about = `Discover products from trusted partner stores at ${brand}. Curated quality across our storefront.`;
      const addr = SITE_FOOTER_FALLBACK.address
        ? [SITE_FOOTER_FALLBACK.address]
        : [];
      return {
        brandName: brand,
        aboutText: about,
        contact: {
          addressLines: addr,
          phone: SITE_FOOTER_FALLBACK.phone || null,
          email: SITE_FOOTER_FALLBACK.email || null,
        },
        socialLinks,
        copyrightYear: year,
      };
    }

    const sid = getFirstShopId(shops);
    const shop = shops.find((s) => s.id === sid);
    const brandName = (displayName || shop?.name || APP_NAME).trim() || APP_NAME;

    const aboutText =
      shop?.description?.trim() ||
      `${brandName} is your destination for quality fashion, accessories, and lifestyle products.`;

    let addressLines = shop ? buildAddressLines(shop) : [];
    if (!addressLines.length && SITE_FOOTER_FALLBACK.address) {
      addressLines = [SITE_FOOTER_FALLBACK.address];
    }

    const phone = shop?.phone || SITE_FOOTER_FALLBACK.phone || null;
    const email = shop?.email || SITE_FOOTER_FALLBACK.email || null;

    return {
      brandName,
      aboutText,
      contact: {
        addressLines,
        phone,
        email,
      },
      socialLinks,
      copyrightYear: year,
    };
  }, [shops, displayName]);
}
